/** Joint-authored motion shared by v6 and D's painted modular artwork.
 * Coordinates are local to the foot anchor; gameplay never mutates here. */
export const MOTIONS = {
  idle: [32, 16, -1],
  walk: [16, 16, -1],
  run: [16, 22, -1],
  start: [8, 20, -1],
  stop: [8, 20, -1],
  turn: [8, 20, -1],
  attack: [12, 30, 4],
  cast: [16, 32, 6],
  hurt: [8, 24, -1],
  stagger: [12, 16, -1],
  death: [16, 20, -1],
  dash: [12, 36, 2],
  chop: [16, 32, 8],
  mine: [16, 32, 9],
  gather: [12, 24, 7],
  craft: [20, 20, 12],
  eat: [20, 20, 10],
  plant: [16, 32, 8],
  water: [16, 32, 6],
  harvest: [16, 24, 9],
  interact: [12, 20, 6],
  rest: [32, 12, -1],
  observe: [24, 12, -1],
  cheer: [20, 20, -1],
  social: [24, 16, -1],
  guard: [16, 16, -1],
  slam: [20, 20, 13],
  rush: [16, 20, 9],
  volley: [20, 20, 12],
  heal: [24, 24, 14],
  wind: [32, 16, -1],
  depleted: [1, 1, -1],
  grow: [24, 12, -1],
} as const;
export type Motion = keyof typeof MOTIONS;
export type Facing = "east" | "west" | "north" | "south";
export const ACTOR_IDS = [
  "hero",
  "pumpkin",
  "coconut",
  "wintermelon",
  "banana",
  "moonherb",
  "carrot",
  "chili",
  "mite",
  "brute",
  "spitter",
  "sentinel",
  "moth",
  "thorn-warden",
  "ember-tortoise",
];
export const OBJECT_IDS = [
  "tree",
  "rock",
  "amber",
  "herb",
  "berry",
  "well",
  "gate",
  "camp",
  ...ACTOR_IDS.slice(1, 8).map((x) => `crop-${x}`),
  ...["fern", "flowers", "root", "log", "reed", "ruin", "stones", "shroom"].map(
    (x) => `decor-${x}`,
  ),
];
export const ASSET_NAMES = [
  "庭园行者",
  "南瓜刀斧手",
  "椰露侍从",
  "冬瓜磐卫",
  "芭蕉叶刃",
  "月露医官",
  "根矢斥候",
  "焰椒术士",
  "荆棘猎螨",
  "裂岩重卫",
  "幽伞孢母",
  "琥甲盾卫",
  "织雾蛾",
  "荆冠守望者",
  "赤晶玄甲",
];
export function applicableMotions(id: string): Motion[] {
  if (id.startsWith("decor-")) return ["wind"];
  if (OBJECT_IDS.includes(id))
    return id.startsWith("crop-")
      ? ["grow", "wind", "harvest", "cast"]
      : ["well", "gate", "camp"].includes(id)
        ? ["wind", "interact"]
        : ["wind", "hurt", "depleted"];
  const base: Motion[] = [
    "idle",
    "walk",
    "run",
    "start",
    "stop",
    "turn",
    "attack",
    "hurt",
    "stagger",
    "death",
    "dash",
    "rest",
    "observe",
  ];
  if (id === "hero")
    return [
      ...base,
      "cast",
      "chop",
      "mine",
      "gather",
      "craft",
      "eat",
      "plant",
      "water",
      "harvest",
      "interact",
      "cheer",
      "social",
      "guard",
    ];
  if (ACTOR_IDS.indexOf(id) < 8)
    return [
      ...base,
      "chop",
      "mine",
      "gather",
      "eat",
      "guard",
      "cast",
      "interact",
      "cheer",
      "social",
      ...(id === "moonherb"
        ? ["heal" as const]
        : id === "wintermelon"
          ? ["slam" as const]
          : []),
    ];
  const enemyBase = base.filter((m) => m !== "dash");
  return [
    ...enemyBase,
    ...(id === "mite"
      ? ["rush" as const]
      : id === "spitter"
        ? ["volley" as const]
        : id === "moth"
          ? ["heal" as const, "volley" as const]
          : id === "sentinel"
            ? ["guard" as const, "rush" as const]
            : id === "brute"
              ? ["slam" as const]
              : ["slam" as const, "rush" as const, "volley" as const]),
  ];
}
export const loopMotion = (m: Motion) =>
  ["idle", "walk", "run", "rest", "observe", "wind", "guard"].includes(m);
export const motionDuration = (m: Motion) => MOTIONS[m][0] / MOTIONS[m][1];
export const contactStride = 60;
export const strideLength = (motion: Motion) =>
  motion === "run" ? 84 : contactStride;
/** Unzoomed world dimensions; cameras scale these uniformly. */
export function displayMetrics(id: string) {
  if (id === "hero") return { width: 136, height: 136, radius: 22 };
  if (ACTOR_IDS.slice(1, 8).includes(id)) {
    const size =
      id === "wintermelon"
        ? 190
        : id === "pumpkin"
          ? 180
          : id === "coconut"
            ? 155
            : id === "banana"
              ? 145
              : id === "moonherb"
                ? 142
                : 132;
    return { width: size, height: size, radius: 22 };
  }
  if (id === "thorn-warden" || id === "ember-tortoise")
    return { width: 250, height: 250, radius: 49 };
  if (ACTOR_IDS.includes(id)) {
    const size =
      id === "brute"
        ? 177
        : id === "sentinel"
          ? 157
          : id === "spitter"
            ? 148
            : id === "moth"
              ? 139
              : 132;
    return { width: size, height: size, radius: id === "brute" ? 31 : 21 };
  }
  if (id === "tree") return { width: 145, height: 160, radius: 0 };
  if (id === "gate") return { width: 114, height: 132, radius: 0 };
  if (id === "well" || id === "camp")
    return { width: 94, height: 94, radius: 0 };
  if (id.startsWith("crop-")) return { width: 95, height: 95, radius: 0 };
  return { width: 99, height: 92, radius: 0 };
}
export type Joint = [number, number];
export interface Pose {
  nearShoulder: Joint;
  farShoulder: Joint;
  hip: Joint;
  shoulder: Joint;
  head: Joint;
  nearKnee: Joint;
  farKnee: Joint;
  nearFoot: Joint;
  farFoot: Joint;
  nearElbow: Joint;
  farElbow: Joint;
  nearHand: Joint;
  farHand: Joint;
}
const bone = (p: Joint, a: number, l: number): Joint => [
  p[0] + Math.sin(a) * l,
  p[1] + Math.cos(a) * l,
];
/** Two equal 19px bones, elbow/knee side is stable. End points are authored, never scaled. */
function knee(root: Joint, foot: Joint): Joint {
  const dx = foot[0] - root[0],
    dy = foot[1] - root[1],
    d = Math.max(0.001, Math.hypot(dx, dy)),
    h = Math.sqrt(Math.max(0, 19 * 19 - (d * d) / 4));
  return [
    (root[0] + foot[0]) / 2 + (dy / d) * h,
    (root[1] + foot[1]) / 2 - (dx / d) * h,
  ];
}
export function jointPose(m: Motion, phase: number): Pose {
  const p = Math.max(0, Math.min(1, phase)),
    a = p * Math.PI * 2,
    pulse = Math.sin(p * Math.PI);
  const moving = m === "walk" || m === "run" || m === "start" || m === "stop";
  const amplitude = m === "run" ? 21 : 15;
  const travel = m === "start" ? p : m === "stop" ? 1 - p : 1;
  const foot = (offset: number): Joint => {
    if (!moving) return [offset ? 8 : -8, 0];
    const t = (p + offset) % 1;
    // Stance (first half) slides backward exactly linearly relative to the moving root.
    return [
      (t < 0.5 ? 1 - 4 * t : -1 + 4 * (t - 0.5)) * amplitude * travel,
      t < 0.5 ? 0 : -Math.sin((t - 0.5) * Math.PI * 2) * (m === "run" ? 13 : 9),
    ];
  };
  let lean = 0,
    crouch = 0,
    arm = -0.18,
    fore = 0.12,
    far = 0.12;
  if (moving) {
    arm = -Math.cos(a) * 0.6 * travel;
    fore = arm + 0.16;
    far = -arm;
  }
  if (m === "attack") {
    const s = p < 0.32 ? -p * 3 : Math.sin(((p - 0.32) / 0.68) * Math.PI) * 1.8;
    arm = s;
    fore = s + 0.6;
    lean = s * 3;
  }
  if (m === "chop" || m === "mine") {
    arm = p < 0.5 ? -p * 4.5 : -2.25 + Math.sin((p - 0.5) * Math.PI) * 3.5;
    fore = arm - 0.4;
    lean = p > 0.5 ? pulse * 5 : 0;
    if (m === "mine") {
      crouch = pulse * 6;
      arm += pulse * 0.4;
      fore += pulse * 0.8;
      lean -= pulse * 3;
    }
  }
  if (m === "cast" || m === "heal" || m === "volley") {
    arm = -pulse * 1.8;
    fore = -pulse * 2.1;
    far = pulse * 1.5;
  }
  if (m === "heal") {
    arm = -pulse * 1.1;
    fore = pulse * 1.6;
    far = pulse * 1.1;
  }
  if (m === "water") {
    arm = pulse * 0.8;
    fore = 1.1 * pulse;
    crouch = 2 * pulse;
  }
  if (m === "plant" || m === "gather" || m === "harvest") {
    crouch = pulse * (m === "gather" ? 6 : 12);
    arm = (m === "gather" ? 0.9 : 0.45) * pulse;
    fore = (m === "harvest" ? -1.1 : m === "gather" ? 0.4 : 0.9) * pulse;
  }
  if (m === "craft") {
    arm = -Math.abs(Math.sin(a * 2)) * 0.8;
    fore = arm - 1;
    crouch = 5;
  }
  if (m === "eat") {
    arm = -pulse * 0.9;
    fore = -pulse * 2.6;
  }
  if (m === "interact") {
    arm = -pulse * 0.45;
    fore = pulse * 1.6;
  }
  if (m === "hurt" || m === "stagger") {
    lean = -pulse * 5;
    arm = -pulse;
    fore = 0.7;
    crouch = pulse * 4;
  }
  if (m === "death") {
    crouch = p * 22;
    lean = -p * 10;
    arm = -p;
    fore = p;
  }
  if (m === "dash" || m === "rush") {
    lean = pulse * 10;
    crouch = pulse * 9;
    arm = -pulse;
    fore = -pulse;
  }
  if (m === "slam") {
    crouch = p < 0.6 ? -pulse * 4 : Math.sin(((p - 0.6) / 0.4) * Math.PI) * 14;
    arm = p < 0.6 ? -pulse * 2.5 : pulse;
    fore = arm;
    far = -arm;
  }
  if (m === "guard") {
    arm = -0.65;
    fore = 1.5;
    far = -0.5;
    crouch = 4;
  }
  if (m === "rest") {
    crouch = 16;
    arm = 0.4;
    fore = -0.8;
  }
  if (m === "observe") {
    fore = -1.6 * pulse;
    arm = -0.7 * pulse;
    lean = Math.sin(a) * 2;
  }
  if (m === "social") {
    arm = -1.8 * pulse;
    fore = -1.9 * pulse + 0.3 * Math.sin(a * 3);
  }
  if (m === "cheer") {
    arm = -pulse * 2.4;
    fore = arm;
    far = -arm;
  }
  if (m === "turn") lean = Math.sin(a) * 2;
  const hip: Joint = [lean, -31 + crouch],
    shoulder: Joint = [lean + 1, -68 + crouch];
  const nearFoot = foot(0),
    farFoot = foot(0.5),
    nearShoulder: Joint = [shoulder[0] + 18, shoulder[1] + 2],
    farShoulder: Joint = [shoulder[0] - 18, shoulder[1] + 2],
    nearElbow = bone(nearShoulder, arm, 18),
    farElbow = bone(farShoulder, far, 18);
  return {
    hip,
    shoulder,
    nearShoulder,
    farShoulder,
    head: [lean, -86 + crouch],
    nearFoot,
    farFoot,
    nearKnee: knee(hip, nearFoot),
    farKnee: knee(hip, farFoot),
    nearElbow,
    farElbow,
    nearHand: bone(nearElbow, fore, 18),
    farHand: bone(farElbow, far - 0.1, 18),
  };
}
